import React, { useState, useEffect } from "react";
import {
  ChevronLeft,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Check,
} from "lucide-react";
import GSTCalculator, { calculateGST } from "../ui/GSTCalculator";
import RazorpayService, {
  RazorpayResponse,
} from "../../services/razorpayService";
import { API_URLS } from "../api/urls";
import { useToast } from "../ui/Toastcontext";

interface PlanSelectionPaymentProps {
  planType: "lite" | "spark" | "ignite" | "thrive";
  userId: number | null;
  authToken: string | null;
  planData: {
    title: string;
    pricingOptions: {
      duration: string;
      price: string;
      originalPrice?: string;
      standardDetailsId: number;
      productId: string;
    }[];
    whatYouGet: string[];
    whatsNotIncluded: string[];
  };
  selectedDuration: string;
  onBack: () => void;
  onPaymentSuccess: (
    planType: "lite" | "spark" | "ignite" | "thrive",
    duration: number,
    paymentDetails: {
      orderId: string;
      paymentId: string;
      amount: number;
      transactionDate: string;
      invoiceNumber?: string;
      invoicePdf?: string;
      userPaymentId?: number;
    },
  ) => void;
  isComplimentary?: boolean;
  preFilledEmail?: string;
  preFilledName?: string;
}

interface UserInfo {
  user_fname: string;
  user_lname: string;
  user_mobileno: string;
  email: string;
  profile: string;
}

interface GetUserInfoResponse {
  success: string;
  message: string;
  user_info: UserInfo;
}

interface UpdateUserInfoResponse {
  success: string;
  message: string;
}

interface PaymentApiResponse {
  success: string;
  message: string;
  order_id: string;
  user_payment_id: number;
  invoice_number?: string;
}

interface UpdatePaymentStatusRequest {
  user_id: number;
  order_id: string;
  user_payment_id: number;
}

interface UpdatePaymentStatusResponse {
  success: string;
  message: string;
  payment_id: string;
  signature_id: string;
  invoice_number: string;
  invoice_pdf: string;
}

const PlanSelectionPayment: React.FC<PlanSelectionPaymentProps> = ({
  planType,
  userId,
  authToken,
  planData,
  selectedDuration,
  onBack,
  onPaymentSuccess,
  isComplimentary = false,
  preFilledEmail,
  preFilledName,
}) => {
  const { showToast } = useToast();
  const [showPlanDetails, setShowPlanDetails] = useState(false);
  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string>("");
  const [isEmailExisting, setIsEmailExisting] = useState<boolean>(false);
  const [isLoadingEmail, setIsLoadingEmail] = useState<boolean>(true);
  const [isLoadingUserInfo, setIsLoadingUserInfo] = useState<boolean>(true);
  const [userName, setUserName] = useState<string>("User Name");
  const [userMobileNo, setUserMobileNo] = useState<string>("");
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<
    "idle" | "processing" | "success" | "failed"
  >("idle");
  const [showBrowserPrompt, setShowBrowserPrompt] = useState(false);
  const [currentOrderId, setCurrentOrderId] = useState<string | null>(null);
  const [currentUserPaymentId, setCurrentUserPaymentId] = useState<
    number | null
  >(null);
  const [preCreatedOrder, setPreCreatedOrder] = useState<{
    orderId: string;
    userPaymentId: number;
    amount: number;
    duration: string;
    standardDetailsId: number;
  } | null>(null);

  const razorpayService = RazorpayService.getInstance();

  // Pre-load the Razorpay script so it's ready before the user taps Pay.
  useEffect(() => {
    razorpayService.preloadScript().catch(() => {});
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchUserInfo();
  }, [userId]);

  // Pre-create the payment order in the background once user info is ready.
  // This lets us call rzp.open() synchronously on tap, which iOS Chrome
  // requires — it blocks popups triggered from async callback chains.
  useEffect(() => {
    if (!userId || isLoadingUserInfo || !isEmailExisting || isComplimentary) return;

    const selectedOption = planData.pricingOptions.find(
      (option) => option.duration === selectedDuration,
    );
    if (!selectedOption) return;

    if (
      preCreatedOrder &&
      preCreatedOrder.duration === selectedDuration &&
      preCreatedOrder.standardDetailsId === selectedOption.standardDetailsId
    ) return;

    setPreCreatedOrder(null);

    let cancelled = false;
    const create = async () => {
      try {
        const basePrice = parseFloat(selectedOption.price);
        const gstCalculation = calculateGST(basePrice);

        const response = await fetch(API_URLS.ADD_USER_PAYMENT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fk_user_id: userId,
            payment_type: "Online",
            amount: parseFloat(gstCalculation.total.toFixed(2)),
            plan_type: "Standard",
            plan_Id: selectedOption.standardDetailsId,
          }),
        });

        if (cancelled || !response.ok) return;
        const result: PaymentApiResponse = await response.json();
        if (cancelled || result.success !== "1" || !result.order_id?.trim()) return;

        setPreCreatedOrder({
          orderId: result.order_id.trim(),
          userPaymentId: result.user_payment_id,
          amount: gstCalculation.total,
          duration: selectedDuration,
          standardDetailsId: selectedOption.standardDetailsId,
        });
      } catch {
        // Silent fail — on-click order creation is the fallback.
      }
    };

    create();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, selectedDuration, isEmailExisting, isLoadingUserInfo]);

  // Build a URL with the auth token so the browser can re-authenticate.
  const buildReauthUrl = (): string => {
    const token = sessionStorage.getItem('triva_auth_token');
    const url = new URL(window.location.href);
    if (token) url.searchParams.set('token', token);
    return url.toString();
  };

  // 'webview' — Triva app in-app browser (Android adds "wv" to UA).
  // 'cct'     — Chrome Custom Tab (WhatsApp deeplinks). window.open() returns null.
  // 'normal'  — regular browser where Razorpay works fine.
  const detectEnvironment = (): 'webview' | 'cct' | 'normal' => {
    const ua = navigator.userAgent;
    if (/Android/i.test(ua) && /\bwv\b/.test(ua)) return 'webview';
    try {
      const popup = window.open('', '_blank', 'width=1,height=1,left=99999,top=99999');
      if (!popup) return 'cct';
      popup.close();
    } catch {
      return 'cct';
    }
    return 'normal';
  };

  const openPageInDefaultBrowser = () => {
    const href = buildReauthUrl();
    if (/Android/i.test(navigator.userAgent)) {
      // intent:// hands off to the Android system browser (works from CCT).
      const withoutProtocol = href.replace(/^https?:\/\//, '');
      window.location.href = `intent://${withoutProtocol}#Intent;scheme=https;action=android.intent.action.VIEW;end`;
    } else {
      window.location.href = href;
    }
  };

  const buildAuthHeaders = (): HeadersInit => {
    if (authToken) {
      return { Authorization: `Bearer ${authToken}` };
    }
    return {};
  };

  const fetchUserInfo = async () => {
    if (!userId) {
      setIsLoadingEmail(false);
      setIsLoadingUserInfo(false);
      return;
    }

    try {
      const getUserResponse = await fetch(`${API_URLS.GET_USER_INFO}${userId}`, {
        headers: buildAuthHeaders(),
      });

      if (!getUserResponse.ok)
        throw new Error("Failed to fetch user information");

      const userData: GetUserInfoResponse = await getUserResponse.json();

      if (userData.success !== "1")
        throw new Error("Failed to fetch user information");

      const fullName =
        `${userData.user_info.user_fname || ""} ${userData.user_info.user_lname || ""}`.trim();
      setUserName(fullName || preFilledName || "User Name");
      setUserMobileNo(userData.user_info.user_mobileno || "");

      if (userData.user_info.email && userData.user_info.email.trim() !== "") {
        setEmail(userData.user_info.email);
        setIsEmailExisting(true);
      } else if (preFilledEmail) {
        setEmail(preFilledEmail);
        setIsEmailExisting(false);
      } else {
        setIsEmailExisting(false);
      }
    } catch (error) {
      console.error("Error fetching user info:", error);
      showToast(
        "error",
        "Failed to load user information. Please refresh the page.",
      );
      setIsEmailExisting(false);
      setUserName(preFilledName || "User Name");
    } finally {
      setIsLoadingEmail(false);
      setIsLoadingUserInfo(false);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (emailError) setEmailError("");
  };

  const updateUserEmail = async (userEmail: string): Promise<boolean> => {
    if (!userId) return false;

    try {
      const getUserResponse = await fetch(`${API_URLS.GET_USER_INFO}${userId}`, {
        headers: buildAuthHeaders(),
      });
      if (!getUserResponse.ok)
        throw new Error("Failed to fetch current user information");

      const userData: GetUserInfoResponse = await getUserResponse.json();
      if (userData.success !== "1")
        throw new Error("Failed to fetch current user information");

      const formData = new FormData();
      formData.append("user_id", userId.toString());
      formData.append("user_fname", userData.user_info.user_fname || "");
      formData.append("user_lname", userData.user_info.user_lname || "");
      formData.append("user_mobileno", userData.user_info.user_mobileno || "");
      formData.append("email", userEmail.trim());
      if (userData.user_info.profile && userData.user_info.profile !== "") {
        formData.append("profile", userData.user_info.profile);
      }

      const updateResponse = await fetch(API_URLS.UPDATE_USER_INFO, {
        method: "POST",
        body: formData,
      });

      if (!updateResponse.ok)
        throw new Error(`HTTP error! status: ${updateResponse.status}`);
      const updateData: UpdateUserInfoResponse = await updateResponse.json();
      if (updateData.success !== "1")
        throw new Error(updateData.message || "Failed to update email");

      return true;
    } catch (error) {
      console.error("Error updating user email:", error);
      setEmailError(
        error instanceof Error ? error.message : "Failed to update email",
      );
      return false;
    }
  };

  const getSelectedPlanPrice = (): number => {
    const option = planData.pricingOptions.find(
      (option) => option.duration === selectedDuration,
    );
    return option ? parseFloat(option.price) : 0;
  };

  const extractDurationInMonths = (planString: string): number => {
    if (planString.includes("1 month")) return 1;
    if (planString.includes("3 month")) return 3;
    if (planString.includes("6 month")) return 6;
    if (planString.includes("1 time")) return 1;
    return 1;
  };

  const updatePaymentStatus = async (
    userId: number,
    orderId: string,
    userPaymentId: number,
  ): Promise<{
    invoiceNumber: string;
    invoicePdf: string;
    paymentId: string;
    signatureId: string;
  }> => {
    try {
      console.log("=== UPDATE PAYMENT STATUS API CALL START ===");

      const updatePaymentData: UpdatePaymentStatusRequest = {
        user_id: userId,
        order_id: orderId,
        user_payment_id: userPaymentId,
      };

      console.log(
        "📤 Request Payload:",
        JSON.stringify(updatePaymentData, null, 2),
      );
      console.log("🌐 API URL:", API_URLS.UPDATE_PAYMENT_STATUS);
      console.log("⏰ Request Timestamp:", new Date().toISOString());

      const response = await fetch(API_URLS.UPDATE_PAYMENT_STATUS, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updatePaymentData),
      });

      console.log("📡 Response Status:", response.status);
      console.log("📡 Response Status Text:", response.statusText);
      console.log(
        "📡 Response Headers:",
        Object.fromEntries(response.headers.entries()),
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ HTTP Error Response Body:", errorText);
        throw new Error(
          `HTTP error! status: ${response.status}, body: ${errorText}`,
        );
      }

      const result: UpdatePaymentStatusResponse = await response.json();

      console.log("📥 Full API Response:", JSON.stringify(result, null, 2));
      console.log("✅ Success Flag:", result.success);
      console.log("💬 Message:", result.message);
      console.log("💳 Payment ID:", result.payment_id);
      console.log("✍️ Signature ID:", result.signature_id);
      console.log("📄 Invoice Number:", result.invoice_number);
      console.log("📁 Invoice PDF Path:", result.invoice_pdf);

      if (result.success !== "1") {
        console.error("❌ API returned unsuccessful response");
        console.error("❌ Success value:", result.success);
        console.error("❌ Message:", result.message);
        throw new Error(result.message || "Failed to update payment status");
      }

      console.log("=== UPDATE PAYMENT STATUS API CALL SUCCESS ===");

      return {
        invoiceNumber: result.invoice_number,
        invoicePdf: result.invoice_pdf,
        paymentId: result.payment_id,
        signatureId: result.signature_id,
      };
    } catch (error) {
      console.error("=== UPDATE PAYMENT STATUS API ERROR ===");
      console.error(
        "❌ Error Type:",
        error instanceof Error ? error.constructor.name : typeof error,
      );
      console.error(
        "❌ Error Message:",
        error instanceof Error ? error.message : String(error),
      );
      console.error("❌ Full Error Object:", error);
      console.error("⏰ Error Timestamp:", new Date().toISOString());
      console.error("=== UPDATE PAYMENT STATUS API ERROR END ===");
      throw error;
    }
  };

  const handlePaymentSuccess = async (
    response: RazorpayResponse,
    userPaymentId: number,
    orderId: string,
  ) => {
    if (onPaymentSuccess && userId) {
      const durationInMonths = extractDurationInMonths(selectedDuration);
      const basePrice = parseFloat(
        planData.pricingOptions.find((opt) => opt.duration === selectedDuration)
          ?.price || "0",
      );
      const finalAmount = calculateGST(basePrice).total;

      try {
        console.log("=== HANDLING PAYMENT SUCCESS ===");
        console.log(
          "📦 Full Razorpay Response Object:",
          JSON.stringify(response, null, 2),
        );
        console.log("🔑 Response Keys:", Object.keys(response));
        console.log("📋 User ID:", userId);
        console.log("📋 User Payment ID:", userPaymentId);
        console.log("📋 Order ID (from state):", orderId);
        console.log("💳 Payment ID:", response.razorpay_payment_id);
        console.log("🔐 Order ID (from Razorpay):", response.razorpay_order_id);
        console.log(
          "✍️ Signature (from Razorpay):",
          response.razorpay_signature,
        );

        if (!orderId) {
          throw new Error("Order ID is missing - cannot update payment status");
        }

        console.log("🚀 Calling UPDATE_PAYMENT_STATUS API...");

        const statusUpdateResult = await updatePaymentStatus(
          userId,
          orderId,
          userPaymentId,
        );

        console.log("✅ Payment status updated successfully");
        console.log("📄 Invoice Number:", statusUpdateResult.invoiceNumber);
        console.log("📁 Invoice PDF:", statusUpdateResult.invoicePdf);
        console.log("💳 Backend Payment ID:", statusUpdateResult.paymentId);
        console.log("✍️ Backend Signature ID:", statusUpdateResult.signatureId);

        const paymentDetails = {
          orderId: orderId,
          paymentId: response.razorpay_payment_id,
          amount: finalAmount,
          transactionDate: new Date().toISOString(),
          invoiceNumber: statusUpdateResult.invoiceNumber,
          invoicePdf: statusUpdateResult.invoicePdf,
          userPaymentId: userPaymentId,
        };

        console.log(
          "✅ Complete Payment Details:",
          JSON.stringify(paymentDetails, null, 2),
        );

        onPaymentSuccess(planType, durationInMonths, paymentDetails);
      } catch (error) {
        console.error("❌ Failed to update payment status:", error);
        console.error(
          "❌ Error details:",
          error instanceof Error ? error.message : String(error),
        );

        const paymentDetails = {
          orderId: orderId,
          paymentId: response.razorpay_payment_id,
          amount: finalAmount,
          transactionDate: new Date().toISOString(),
          userPaymentId: userPaymentId,
        };

        console.log("⚠️ Proceeding without invoice number due to error");
        console.log(
          "⚠️ Payment Details:",
          JSON.stringify(paymentDetails, null, 2),
        );

        onPaymentSuccess(planType, durationInMonths, paymentDetails);
      }
    }
  };

  const handlePaymentFailure = async (error: any) => {
    console.error('🔴 handlePaymentFailure called. Raw error:', JSON.stringify(error, null, 2));
    console.error('  error.code:', error?.error?.code);
    console.error('  error.description:', error?.error?.description);
    console.error('  error.source:', error?.error?.source);
    console.error('  error.step:', error?.error?.step);
    console.error('  error.reason:', error?.error?.reason);

    setPaymentStatus("failed");
    setIsProcessingPayment(false);

    if (userId && currentUserPaymentId && currentOrderId) {
      try {
        await updatePaymentStatus(userId, currentOrderId, currentUserPaymentId);
      } catch (updateError) {
        console.error(
          "Failed to update payment status to failed:",
          updateError,
        );
      }
    }

    setCurrentOrderId(null);
    setCurrentUserPaymentId(null);
    showToast("error", "Payment failed. Please try again.");
  };

  const handlePaymentDismiss = async () => {
    console.log('🟠 handlePaymentDismiss called — userId:', userId, 'orderId:', currentOrderId, 'paymentId:', currentUserPaymentId);
    setIsProcessingPayment(false);

    if (userId && currentUserPaymentId && currentOrderId) {
      // Modal was closed after a payment attempt — treat as failure
      setPaymentStatus("failed");
      try {
        await updatePaymentStatus(userId, currentOrderId, currentUserPaymentId);
      } catch (updateError) {
        console.error(
          "Failed to update payment status:",
          updateError,
        );
      }
      setCurrentOrderId(null);
      setCurrentUserPaymentId(null);
      showToast("error", "Payment was not completed. Please try again.");
    } else {
      // No payment was attempted — user cancelled from the form
      setPaymentStatus("idle");
    }
  };

  // Synchronous payment path — no await before rzp.open().
  // iOS Chrome blocks popups triggered from async callbacks; keeping the
  // call stack synchronous from the tap event preserves the user-gesture
  // context that the browser requires to allow the popup.
  const openRazorpaySynchronously = (order: {
    orderId: string;
    userPaymentId: number;
    amount: number;
    duration: string;
    standardDetailsId: number;
  }) => {
    if (!userId || isProcessingPayment) return;

    const selectedOption = planData.pricingOptions.find(
      (option) => option.duration === selectedDuration,
    );
    if (!selectedOption) return;

    // CCT is Android-only. Skip the window.open() probe on iOS so we don't
    // spend the browser's per-gesture popup budget before Razorpay uses it.
    const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
    if (!isIOS && window.innerWidth < 768 && detectEnvironment() === 'cct') {
      setShowBrowserPrompt(true);
      return;
    }

    const { orderId, userPaymentId } = order;
    setIsProcessingPayment(true);
    setPaymentStatus("processing");
    setCurrentOrderId(orderId);
    setCurrentUserPaymentId(userPaymentId);
    setPreCreatedOrder(null);

    const paymentOptions = razorpayService.createPaymentOptions({
      orderId,
      amount: order.amount,
      description: `${planData.title} - ${selectedDuration}`,
      userDetails: {
        name: userName,
        email: email,
        contact: userMobileNo || "",
      },
      onSuccess: (razorpayResponse: RazorpayResponse) =>
        handlePaymentSuccess(razorpayResponse, userPaymentId, orderId),
      onFailure: async (error: any) => {
        console.error('🔴 Sync path payment failure:', error);
        setPaymentStatus("failed");
        setIsProcessingPayment(false);
        if (userId) {
          try { await updatePaymentStatus(userId, orderId, userPaymentId); } catch {}
        }
        setCurrentOrderId(null);
        setCurrentUserPaymentId(null);
        showToast("error", "Payment failed. Please try again.");
      },
      onDismiss: async () => {
        setIsProcessingPayment(false);
        setPaymentStatus("failed");
        if (userId) {
          try { await updatePaymentStatus(userId, orderId, userPaymentId); } catch {}
        }
        setCurrentOrderId(null);
        setCurrentUserPaymentId(null);
        showToast("error", "Payment was not completed. Please try again.");
      },
      notes: {
        plan_type: planType,
        duration: selectedDuration,
        user_payment_id: userPaymentId,
        standard_details_id: selectedOption.standardDetailsId,
      },
    });

    razorpayService.openPaymentSync(paymentOptions);
  };

  const handleProceedToPayment = async () => {
    if (isComplimentary) {
      if (!userId) return;

      if (!isEmailExisting) {
        if (!email.trim()) {
          setEmailError("Email is required");
          return;
        }
        if (!validateEmail(email)) {
          setEmailError("Please enter a valid email address");
          return;
        }
        const emailUpdated = await updateUserEmail(email);
        if (!emailUpdated) {
          showToast("error", "Failed to update email. Please try again.");
          return;
        }
        setIsEmailExisting(true);
      }

      const selectedOption = planData.pricingOptions.find(
        (opt) => opt.duration === selectedDuration,
      );
      if (!selectedOption) {
        showToast("error", "Plan details not found. Please try again.");
        return;
      }

      setIsProcessingPayment(true);
      setPaymentStatus("processing");

      try {
        const res = await fetch(API_URLS.ADD_USER_PAYMENT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            fk_user_id: userId,
            payment_type: "Online",
            amount: 0,
            payment_status: "pending",
            plan_type: "Standard",
            plan_Id: selectedOption.standardDetailsId,
          }),
        });

        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const result: PaymentApiResponse = await res.json();

        if (result.success !== "1") {
          const msg = result.message || "Failed to activate plan";
          setPaymentStatus("failed");
          setIsProcessingPayment(false);
          showToast("error", msg);
          return;
        }

        await new Promise((resolve) => setTimeout(resolve, 10000));

        const durationInMonths = extractDurationInMonths(selectedDuration);
        onPaymentSuccess(planType, durationInMonths, {
          orderId: result.order_id || "",
          paymentId: "",
          amount: 0,
          transactionDate: new Date().toISOString(),
          invoiceNumber: result.invoice_number,
          userPaymentId: result.user_payment_id,
        });
      } catch (error) {
        setPaymentStatus("failed");
        setIsProcessingPayment(false);
        showToast("error", "Failed to activate plan. Please try again.");
      }
      return;
    }

    if (!isEmailExisting) {
      if (!email.trim()) {
        setEmailError("Email is required");
        return;
      }

      if (!validateEmail(email)) {
        setEmailError("Please enter a valid email address");
        return;
      }

      const emailUpdated = await updateUserEmail(email);
      if (!emailUpdated) {
        showToast("error", "Failed to update email. Please try again.");
        return;
      }
      setIsEmailExisting(true);
    }

    // On mobile, check for Chrome Custom Tab before launching Razorpay.
    // CCT blocks window.open() which Razorpay needs for the bank 3DS popup.
    if (window.innerWidth < 768 && detectEnvironment() === 'cct') {
      setShowBrowserPrompt(true);
      return;
    }

    await initiateRazorpayPayment();
  };

  const initiateRazorpayPayment = async () => {
    if (!userId || isProcessingPayment) return;

    const basePrice = parseFloat(
      planData.pricingOptions.find((opt) => opt.duration === selectedDuration)
        ?.price || "0",
    );
    const gstCalculation = calculateGST(basePrice);
    const selectedOption = planData.pricingOptions.find(
      (option) => option.duration === selectedDuration,
    );

    if (!selectedOption) {
      showToast("error", "Please select a valid plan option");
      return;
    }

    const paymentData = {
      fk_user_id: userId,
      payment_type: "Online",
      amount: parseFloat(gstCalculation.total.toFixed(2)),
      plan_type: "Standard",
      plan_Id: selectedOption.standardDetailsId,
    };

    setIsProcessingPayment(true);
    setPaymentStatus("processing");

    try {
      console.log("=== ADD_USER_PAYMENT API CALL START ===");
      console.log("📤 Request Payload:", JSON.stringify(paymentData, null, 2));
      console.log("🌐 API URL:", API_URLS.ADD_USER_PAYMENT);
      console.log("⏰ Request Timestamp:", new Date().toISOString());

      const response = await fetch(API_URLS.ADD_USER_PAYMENT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(paymentData),
      });

      console.log("📡 Response Status:", response.status);
      console.log(
        "📡 Response Headers:",
        Object.fromEntries(response.headers.entries()),
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ HTTP Error Response:", errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: PaymentApiResponse = await response.json();

      console.log(
        "📥 Full ADD_USER_PAYMENT Response:",
        JSON.stringify(result, null, 2),
      );
      console.log("✅ Success Flag:", result.success);
      console.log("💬 Message:", result.message);
      console.log("📋 Order ID (raw):", result.order_id);
      console.log("📋 Order ID (type):", typeof result.order_id);
      console.log(
        "📋 Order ID (length):",
        result.order_id ? result.order_id.length : "N/A",
      );
      console.log("📋 User Payment ID:", result.user_payment_id);
      console.log("📋 Response Keys:", Object.keys(result));

      if (result.success !== "1") {
        console.error("❌ API returned unsuccessful response");
        throw new Error(result.message || "Failed to create order");
      }

      if (!result.order_id || result.order_id.trim() === "") {
        console.error("❌ CRITICAL: order_id is empty in API response!");
        console.error("Full response:", result);
        throw new Error(
          "Order ID is missing from payment creation response. Cannot proceed with payment.",
        );
      }

      const createdOrderId = result.order_id.trim();
      const createdUserPaymentId = result.user_payment_id;

      console.log("=== ADD_USER_PAYMENT API CALL SUCCESS ===");
      console.log("✅ Payment order created successfully:");
      console.log("📋 Order ID:", createdOrderId);
      console.log("📋 User Payment ID:", createdUserPaymentId);
      console.log("💰 Amount:", gstCalculation.total);

      setCurrentOrderId(createdOrderId);
      setCurrentUserPaymentId(createdUserPaymentId);

      console.log("🚀 Creating Razorpay payment options...");

      const paymentOptions = razorpayService.createPaymentOptions({
        orderId: createdOrderId,
        amount: gstCalculation.total,
        description: `${planData.title} - ${selectedDuration}`,
        userDetails: {
          name: userName,
          email: email,
          contact: userMobileNo || "",
        },
        onSuccess: (razorpayResponse: RazorpayResponse) =>
          handlePaymentSuccess(
            razorpayResponse,
            createdUserPaymentId,
            createdOrderId,
          ),
        onFailure: handlePaymentFailure,
        onDismiss: handlePaymentDismiss,
        notes: {
          plan_type: planType,
          duration: selectedDuration,
          user_payment_id: createdUserPaymentId,
          standard_details_id: selectedOption.standardDetailsId,
        },
      });

      await razorpayService.initiatePayment(paymentOptions);
    } catch (error) {
      console.error("=== ADD_USER_PAYMENT OR RAZORPAY ERROR ===");
      console.error(
        "❌ Error Type:",
        error instanceof Error ? error.constructor.name : typeof error,
      );
      console.error(
        "❌ Error Message:",
        error instanceof Error ? error.message : String(error),
      );
      console.error("❌ Full Error:", error);

      setPaymentStatus("failed");
      setIsProcessingPayment(false);

      const errorMessage =
        error instanceof Error ? error.message : "Unknown error occurred";
      showToast("error", `Payment initiation failed: ${errorMessage}`);
    }
  };

  const selectedPlanPrice = getSelectedPlanPrice();
  const gstCalculation = calculateGST(selectedPlanPrice);

  return (
    <div className="min-h-screen bg-white px-[24px] mt-[32px]">
      {/* CCT prompt (WhatsApp deeplinks) — intent:// opens page in system browser */}
      {showBrowserPrompt && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl px-8 py-10 mx-6 flex flex-col items-center shadow-xl">
            <h2 className="text-[18px] font-bold text-[#202020] mb-2 text-center">
              Open in browser to pay
            </h2>
            <p className="text-[#646464] text-sm text-center mb-6 leading-relaxed">
              Payment requires your browser. Tap below to continue in Chrome.
            </p>
            <button
              onClick={openPageInDefaultBrowser}
              className="w-full bg-[#0DB5B5] text-white py-3 rounded-xl font-medium mb-3"
            >
              Open in Browser
            </button>
            <button
              onClick={() => setShowBrowserPrompt(false)}
              className="text-[#646464] text-sm"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Processing transaction overlay */}
      {paymentStatus === "processing" && isProcessingPayment && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20">
          <div className="bg-white rounded-2xl px-10 py-12 flex flex-col items-center shadow-lg mx-6">
            <div className="w-2 h-2 rounded-full bg-gray-300 mb-6 animate-pulse" />
            <h2 className="text-xl font-bold text-[#202020] mb-3 text-center">
              Processing your transaction
            </h2>
            <p className="text-[#646464] text-center text-sm leading-relaxed">
              Please do not refresh, go back,<br />or click anywhere.
            </p>
          </div>
        </div>
      )}

      <div className="flex items-center bg-white mb-4">
        <button
          onClick={onBack}
          className="flex items-center gap-1"
          disabled={isProcessingPayment}
        >
          <ChevronLeft className="w-6 h-6 text-[#173430]" />
          <span className="text-[14px] font-medium text-[#202020]">Back</span>
        </button>
      </div>

      <div className="pb-32">
        <div className="mb-4">
          <label className="text-[14px] font-medium text-[#202020] block">
            Full Name
          </label>
          {isLoadingUserInfo ? (
            <div className="flex items-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0DB5B5] mr-2"></div>
              <span className="text-[14px] text-gray-500">Loading...</span>
            </div>
          ) : (
            <p className="text-[16px] text-[#8D8D8D]">{userName}</p>
          )}
        </div>

        <div className="mb-4">
          <label className="text-[14px] font-medium text-[#202020] block">
            Subscription plan
          </label>
          <p className="text-[16px] text-[#8D8D8D]">
            {planData.title}
            {selectedDuration &&
              ` (${selectedDuration.replace(/For\s+/i, "").replace(/\s*@.*$/, "")})`}
          </p>
        </div>

        <div className="mb-6">
          <label className="text-[14px] font-medium text-[#202020] mb-2 block">
            Email {!isEmailExisting && <span className="text-red-500">*</span>}
          </label>
          {isLoadingEmail ? (
            <div className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-gray-50">
              <div className="flex items-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-[#0DB5B5] mr-2"></div>
                <span className="text-[14px] text-gray-500">
                  Loading email...
                </span>
              </div>
            </div>
          ) : (
            <>
              <input
                type="email"
                value={email}
                onChange={handleEmailChange}
                placeholder={isEmailExisting ? "" : "Enter your email"}
                disabled={isEmailExisting || isProcessingPayment}
                className={`w-full px-4 py-3 rounded-lg text-[16px] transition-colors ${
                  isEmailExisting || isProcessingPayment
                    ? "bg-gray-50 border border-gray-300 text-gray-700 cursor-not-allowed"
                    : emailError
                      ? "border-2 border-red-500 focus:outline-none focus:border-red-500"
                      : "border border-gray-300 focus:outline-none focus:border-[#0DB5B5]"
                }`}
              />
              {isEmailExisting && (
                <p className="mt-2 text-[12px] text-gray-500">
                  This email is registered with your account
                </p>
              )}
              {emailError && !isEmailExisting && (
                <p className="mt-2 text-[14px] text-red-500">{emailError}</p>
              )}
            </>
          )}
        </div>

        <div className="border border-gray-200 rounded-lg mb-6">
          <button
            onClick={() =>
              !isProcessingPayment && setShowPlanDetails(!showPlanDetails)
            }
            disabled={isProcessingPayment}
            className="w-full px-4 py-4 flex items-center justify-between text-left"
          >
            <span className="text-[16px] font-medium text-[#202020]">
              Plan details
            </span>
            {showPlanDetails ? (
              <ChevronUp className="w-5 h-5 text-[#202020]" />
            ) : (
              <ChevronDown className="w-5 h-5 text-[#202020]" />
            )}
          </button>

          {showPlanDetails && (
            <div className="px-4 pb-4 border-t border-gray-100">
              <div className="mb-6 mt-4">
                <h4 className="text-[14px] font-semibold text-[#202020] mb-3">
                  What you get:
                </h4>
                <div className="space-y-3">
                  {planData.whatYouGet.map((item, index) => (
                    <div key={index} className="flex items-start">
                      <Check className="w-4 h-4 text-[#0DB5B5] mr-3 mt-0.5 flex-shrink-0" />
                      <span className="text-[14px] text-[#202020] leading-relaxed">
                        {item}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {planData.whatsNotIncluded.length > 0 && (
                <div>
                  <h4 className="text-[14px] font-semibold text-[#202020] mb-3">
                    What's not included:
                  </h4>
                  <div className="space-y-3">
                    {planData.whatsNotIncluded.map((item, index) => (
                      <div key={index} className="flex items-start">
                        <AlertCircle className="w-4 h-4 text-[#FF3B30] mr-3 mt-0.5 flex-shrink-0" />
                        <span className="text-[14px] text-[#202020] leading-relaxed">
                          {item}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex items-center mb-6 text-sm text-[#202020]">
          <AlertCircle className="w-4 h-4 mr-2" />
          <span>No auto-renewal. Top-up anytime.</span>
        </div>

        {(selectedPlanPrice > 0 || isComplimentary) && (
          <GSTCalculator baseAmount={selectedPlanPrice} className="mb-6" />
        )}

        {paymentStatus === "success" && (
          <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center text-green-800">
              <Check className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">
                Payment completed successfully!
              </span>
            </div>
          </div>
        )}

        {paymentStatus === "failed" && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center text-red-800">
              <AlertCircle className="w-4 h-4 mr-2" />
              <span className="text-sm font-medium">
                Payment failed. Please try again.
              </span>
            </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-0 right-0 bg-white px-[24px] py-4 border-t border-gray-100">
        <div className="flex gap-3">
          <button
            onClick={onBack}
            disabled={isProcessingPayment}
            className={`flex-1 border border-gray-300 text-[#646464] py-3 rounded-xl font-medium transition-colors ${
              isProcessingPayment
                ? "opacity-50 cursor-not-allowed"
                : "hover:bg-gray-50"
            }`}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              // Use the synchronous path when a pre-created order and the
              // Razorpay script are both ready. This keeps rzp.open() inside
              // the tap event's synchronous call stack, which iOS Chrome
              // requires to allow the payment popup.
              if (
                !isComplimentary &&
                preCreatedOrder &&
                preCreatedOrder.duration === selectedDuration &&
                razorpayService.isScriptReady() &&
                isEmailExisting
              ) {
                openRazorpaySynchronously(preCreatedOrder);
              } else {
                handleProceedToPayment();
              }
            }}
            disabled={
              !userId ||
              isLoadingEmail ||
              isLoadingUserInfo ||
              (!isComplimentary && selectedPlanPrice <= 0) ||
              isProcessingPayment ||
              paymentStatus === "success"
            }
            className={`flex-1 py-3 rounded-xl font-medium transition-colors ${
              paymentStatus === "success"
                ? "bg-green-500 text-white"
                : paymentStatus === "failed"
                  ? "bg-[#0DB5B5] text-white hover:bg-[#0A9999]"
                  : !userId ||
                      isLoadingEmail ||
                      isLoadingUserInfo ||
                      (!isComplimentary && selectedPlanPrice <= 0) ||
                      isProcessingPayment
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-[#0DB5B5] text-white hover:bg-[#0A9999]"
            }`}
          >
            {isProcessingPayment ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {isComplimentary ? "Activating..." : "Processing..."}
              </div>
            ) : isLoadingEmail || isLoadingUserInfo ? (
              "Loading..."
            ) : paymentStatus === "success" ? (
              "Payment Successful!"
            ) : paymentStatus === "failed" ? (
              isComplimentary ? "Activation Failed - Retry" : "Retry Payment"
            ) : isComplimentary ? (
              "Activate Complimentary Plan"
            ) : (
              `Pay ₹${gstCalculation.total.toFixed(2)}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export { PlanSelectionPayment };
